import dayjs from 'dayjs';
import 'dayjs/locale/de.js';
import isToday from 'dayjs/plugin/isToday.js';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import { BookingDeclineReason } from '../appointments/enums/booking-decline-reason.enum.js';
import { getPath } from '../routing/route-type-map.js';
import { NotificationType } from './enum/notificationType.enum.js';
dayjs.extend(isToday);
dayjs.extend(relativeTime);
/** German copy for the fixed booking-decline reasons, shown to the patient verbatim. */
const BOOKING_DECLINE_REASON_TEXT = {
    [BookingDeclineReason.SLOT_UNAVAILABLE]: 'Der gewünschte Termin ist nicht mehr verfügbar.',
    [BookingDeclineReason.NO_CAPACITY]: 'Die Praxis hat aktuell keine freie Kapazität.',
    [BookingDeclineReason.THERAPIST_UNAVAILABLE]: 'Die gewünschte Therapeut:in ist nicht verfügbar.',
    [BookingDeclineReason.OTHER]: 'Bitte kontaktiere die Praxis für Rückfragen.',
};
// --- Colour vocabulary -------------------------------------------------------
// String-literal values of the ColorVariants enum, asserted to the enum type.
// Runtime-safe (no value import of design-tokens); the test suite asserts each
// equals the real `ColorVariants` member so a typo cannot slip through.
const VARIANT = {
    /** negative / cancelled / failed */
    ERROR: 'error',
    /** time-related: reminders, schedules */
    WARNING: 'warning',
    /** appointments + simple informational (petrol) */
    ACTIVE: 'active',
    DESTRUCTIVE: 'destructive',
    MOST_IMPORTANT: 'most-important',
};
// --- Icon table (ADR-0019) ---------------------------------------------------
const DEFAULT_ICON = 'icon-[mingcute--notification-line]';
const ICONS = {
    [NotificationType.APPOINTMENT_RESCHEDULED]: 'icon-[mingcute--calendar-line]',
    [NotificationType.BOOKING_CONFIRMED]: 'icon-[mingcute--calendar-line]',
    [NotificationType.BOOKING_DECLINED]: 'icon-[mingcute--calendar-x-line]',
    [NotificationType.BOOKING_AUTO_CANCELLED]: 'icon-[mingcute--calendar-x-line]',
    [NotificationType.APPOINTMENT_ACCEPT_SUCCESS]: 'icon-[mingcute--calendar-line]',
    [NotificationType.APPOINTMENT_ACCEPTED]: 'icon-[mingcute--calendar-line]',
    [NotificationType.APPOINTMENT_INVITE]: 'icon-[mingcute--calendar-line]',
    [NotificationType.CALL_BEGINNING_REMINDER]: 'icon-[mingcute--alarm-2-line]',
    [NotificationType.CALL_CANCELED]: 'icon-[mingcute--calendar-x-line]',
    [NotificationType.APPOINTMENT_DECLINE_SUCCESS]: 'icon-[mingcute--calendar-x-line]',
    [NotificationType.APPOINTMENT_DECLINED]: 'icon-[mingcute--calendar-x-line]',
    [NotificationType.APPOINTMENT_CANCELLED_BY_PATIENT]: 'icon-[mingcute--calendar-x-line]',
    [NotificationType.COMPLETED_EXERCISE_PROGRAM]: 'icon-[mingcute--check-line]',
    [NotificationType.CRITICAL_PROGRAM_VALUES]: 'icon-[mingcute--user-warning-line]',
    [NotificationType.OFFICE_USER_REGISTER]: 'icon-[mingcute--group-3-line]',
    [NotificationType.EXERCISE_PROGRAM_UPDATED]: 'icon-[mingcute--refresh-anticlockwise-1-line]',
    [NotificationType.NEW_EXERCISE_PROGRAM]: 'icon-[mingcute--play-line]',
    [NotificationType.NEW_INVOICE_AVAILABLE]: 'icon-[mingcute--bill-line]',
    [NotificationType.NEW_OFFICE_EXERCISE]: 'icon-[mingcute--inbox-2-line]',
    [NotificationType.NEW_PRODUCT_ASSIGNMENT]: 'icon-[mingcute--shopping-cart-2-line]',
    [NotificationType.PRODUCT_ASSIGNMENT_7_DAY_WARNING]: 'icon-[mingcute--shopping-cart-2-line]',
    [NotificationType.NEW_PROGRAM_STATS]: 'icon-[mingcute--chart-bar-line]',
    [NotificationType.PATIENT_HAND_OVER]: 'icon-[mingcute--user-add-line]',
    [NotificationType.PATIENT_HAND_OVER_AS_PATIENT]: 'icon-[mingcute--user-add-2-line]',
    [NotificationType.PATIENT_REGISTER]: 'icon-[mingcute--user-2-line]',
    [NotificationType.PATIENT_REMOVED]: 'icon-[mingcute--user-remove-line]',
    [NotificationType.PROGRAM_EXPIRED]: 'icon-[mingcute--sandglass-2-line]',
};
// --- Titles ------------------------------------------------------------------
// Ported from the frontend `TITLES` map; the three invoice/agreement types that
// only ever surfaced via push/email keep their backend subject wording.
const TITLES = {
    [NotificationType.APPOINTMENT_ACCEPTED]: 'Termin angenommen',
    [NotificationType.APPOINTMENT_ACCEPT_SUCCESS]: 'Termin angenommen',
    [NotificationType.APPOINTMENT_DECLINE_SUCCESS]: 'Termin abgelehnt',
    [NotificationType.APPOINTMENT_DECLINED]: 'Termin abgelehnt',
    [NotificationType.APPOINTMENT_CANCELLED_BY_PATIENT]: 'Termin abgesagt',
    [NotificationType.CALL_CANCELED]: 'Termin abgesagt',
    [NotificationType.CALL_BEGINNING_REMINDER]: 'Terminerinnerung',
    [NotificationType.CALL_THERAPIST_HANDOVER_ACTIVE]: 'Termin zugewiesen',
    [NotificationType.CALL_THERAPIST_HANDOVER_PASSIVE_PATIENT]: 'Termin übernommen',
    [NotificationType.CALL_THERAPIST_HANDOVER_PASSIVE_THERAPIST]: 'Termin übernommen',
    [NotificationType.APPOINTMENT_RESCHEDULED]: 'Termin verschoben',
    [NotificationType.BOOKING_CONFIRMED]: 'Termin bestätigt',
    [NotificationType.BOOKING_DECLINED]: 'Termin abgelehnt',
    [NotificationType.BOOKING_AUTO_CANCELLED]: 'Termin storniert',
    [NotificationType.APPOINTMENT_INVITE]: 'Termineinladung',
    [NotificationType.COMPLETED_EXERCISE_PROGRAM]: 'Übungsprogramm',
    [NotificationType.EXERCISE_PROGRAM_UPDATED]: 'Übungsprogramm',
    [NotificationType.NEW_EXERCISE_PROGRAM]: 'Übungsprogramm',
    [NotificationType.PROGRAM_EXPIRED]: 'Programm abgelaufen',
    [NotificationType.EXERCISE_PROGRAM_COMPLETION_REMINDER]: 'Erinnerung',
    [NotificationType.TODAYS_EXERCISE_SCHEDULE]: 'Übungen noch offen',
    [NotificationType.CRITICAL_PROGRAM_VALUES]: 'Kritischer Wert',
    [NotificationType.NEW_PROGRAM_STATS]: 'Neue Statistik',
    [NotificationType.NEW_OFFICE_EXERCISE]: 'Neue Übung',
    [NotificationType.PATIENT_REGISTER]: 'Registrierung',
    [NotificationType.OFFICE_USER_REGISTER]: 'Registrierung',
    [NotificationType.PATIENT_HAND_OVER]: 'Patient:in zugewiesen',
    [NotificationType.PATIENT_HAND_OVER_AS_PATIENT]: 'Überweisung',
    [NotificationType.PATIENT_REMOVED]: 'Patient:in entfernt',
    [NotificationType.NEW_INVOICE_AVAILABLE]: 'Rechnung',
    [NotificationType.NEW_PRODUCT_ASSIGNMENT]: 'Praxis-Shop',
    [NotificationType.PRODUCT_ASSIGNMENT_7_DAY_WARNING]: 'Leistung läuft aus',
    [NotificationType.PATIENT_INVOICE_ISSUED]: 'Neue Rechnung von deiner Praxis',
    [NotificationType.PATIENT_INVOICE_CANCELLED]: 'Stornorechnung von deiner Praxis',
    [NotificationType.HONORARVEREINBARUNG_SENT]: 'Ihre Honorarvereinbarung',
};
// --- Interpolation helpers ---------------------------------------------------
// Explicit `.locale('de')` per call; no global dayjs locale mutation, so moving
// these into shared cannot affect other consumers of the process-wide dayjs.
const de = (value) => dayjs(value).locale('de');
export const getScheduledStart = (n) => de(n.call?.scheduledStart).format('DD. MMMM YYYY, HH:mm');
const formatDateTime = (date) => `${de(date).format('DD.MM.YYYY')}, ${de(date).format('HH:mm')} Uhr`;
export const getRescheduleFrom = (n) => formatDateTime(n.previousScheduledStart);
export const getRescheduleTo = (n) => formatDateTime(n.newScheduledStart);
/** Time label: today → relative ("vor 2 Stunden"), otherwise the date. */
export const getNotificationTimeLabel = (n) => {
    if (!n.actionTime)
        return undefined;
    const d = de(n.actionTime);
    return d.isToday() ? d.fromNow() : d.format('DD. MMMM YYYY');
};
const dativ = (u) => (u?.salutation === 'Herr' ? 'Herrn' : u?.salutation || u?.firstName) || '';
export const getTherapistSalutationDativCall = (n) => dativ(n.call?.therapist);
export const getNewTherapistSalutationDativ = (n) => dativ(n.newTherapist);
export const getActorSalutationDativ = (n) => dativ(n.actor);
export const getPatientText = (n) => {
    const patients = n.call?.callPatients ?? [];
    if (patients.length === 1) {
        const p = patients[0]?.patient;
        const salutation = p?.salutation === 'Herr' ? 'Herrn' : p?.salutation || p?.firstName;
        return `${salutation} ${p?.lastName}`;
    }
    return `${patients.length} Patient:innen`;
};
export const getActorText = (n) => n.actorId === n.call?.therapistId
    ? 'Du hast dich selbst'
    : `${n.actor?.salutation || n.actor?.firstName} ${n.actor?.lastName} hat dir`;
const actorName = (n) => `${n.actor?.firstName ?? ''} ${n.actor?.lastName ?? ''}`.trim();
const programIndex = (n) => `${n.exercisePrograms?.[0]?.index ?? ''}`;
/**
 * The button on a patient's Übungsprogramm notification.
 *
 * Names exactly one program → straight into that program's player, the same
 * route the program list's own start gesture pushes, so the training starts
 * instead of the patient landing on the overview to find again what the
 * notification just told them about. Names several (or none) → the overview,
 * where they pick, because there is nothing single to start; that case keeps the
 * caller's own wording, which is the plural one.
 *
 * An `href` rather than a fourth `actionKey`: the path carries the program id,
 * which a static action-key table cannot, and the same string is what makes the
 * email link deep-link too (see `resolveButtonHref`).
 */
const exerciseProgramButton = (n, overviewText) => {
    const programs = n.exercisePrograms ?? [];
    const exerciseProgramId = programs.length === 1 ? programs[0]?.id : undefined;
    if (!exerciseProgramId) {
        return {
            text: overviewText,
            colorVariant: VARIANT.ACTIVE,
            actionKey: 'OPEN_EXERCISE_EXECUTION',
        };
    }
    return {
        text: 'Jetzt starten',
        colorVariant: VARIANT.ACTIVE,
        href: getPath('/exercise-execution/:exerciseProgramId/player', {
            exerciseProgramId,
        }),
    };
};
/**
 * Appends the therapist's "Information für den Patienten" to a program
 * notification. Returns the `text` (plain fallback for push/email) and, when a
 * message exists, `segments` so the in-app banner can render the label
 * "Nachricht:" on its own line and the message itself underlined.
 */
const withPatientMessage = (baseText, n) => {
    const message = n.exerciseProgramMessage?.message?.trim();
    if (!message) {
        return { text: baseText };
    }
    return {
        text: `${baseText}\n\nNachricht:\n${message}`,
        segments: [
            { text: baseText },
            { text: 'Nachricht:', class: 'block font-semibold underline' },
            { text: message, class: 'block' },
        ],
    };
};
const todaysScheduleLabel = (n) => {
    const programs = n.exercisePrograms ?? [];
    const first = `Programm#${programs[0]?.index}`;
    const second = programs.length > 1 ? `, Programm#${programs[1]?.index}` : '';
    const more = programs.length > 2 ? ` und ${programs.length - 2} weitere` : '';
    return `(${first}${second}${more})`;
};
const therapistInCall = (n) => `${getTherapistSalutationDativCall(n)} ${n.call?.therapist?.lastName ?? ''}`;
const newTherapistName = (n) => `${getNewTherapistSalutationDativ(n)} ${n.newTherapist?.lastName ?? ''}`;
// --- Appointment-sourced helpers (appointment participation types) -----------
// Read the appointment header rather than the video-call (`Call`) extension, so
// the invite / accept / decline notifications compose for in-person appointments.
export const getAppointmentScheduledStart = (n) => de(n.appointment?.scheduledStart).format('DD. MMMM YYYY, HH:mm');
const therapistInAppointment = (n) => `${dativ(n.appointment?.therapist)} ${n.appointment?.therapist?.lastName ?? ''}`.trim();
// Nominativ (subject) form for sentences where the therapist is acting, e.g. the
// appointment invite ("Herr Müller hat dich eingeladen").
const therapistInAppointmentNominativ = (n) => `${n.appointment?.therapist?.salutation || n.appointment?.therapist?.firstName || ''} ${n.appointment?.therapist?.lastName ?? ''}`.trim();
const getBody = (n) => {
    switch (n.type) {
        case NotificationType.APPOINTMENT_ACCEPTED:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ACTIVE,
                text: `${actorName(n)} hat den Termin am ${getAppointmentScheduledStart(n)} Uhr bestätigt.`,
            };
        case NotificationType.APPOINTMENT_ACCEPT_SUCCESS:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ACTIVE,
                text: `Du hast den Termin mit ${therapistInAppointment(n)} am ${getAppointmentScheduledStart(n)} erfolgreich bestätigt.`,
            };
        case NotificationType.APPOINTMENT_DECLINED:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ERROR,
                text: `${actorName(n)} hat den Termin am ${getAppointmentScheduledStart(n)} Uhr abgelehnt.`,
            };
        case NotificationType.APPOINTMENT_CANCELLED_BY_PATIENT:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ERROR,
                text: n.appointmentCancelled === false
                    ? `${actorName(n)} hat die Teilnahme am Termin am ${getAppointmentScheduledStart(n)} Uhr abgesagt.`
                    : `${actorName(n)} hat den Termin am ${getAppointmentScheduledStart(n)} Uhr abgesagt.`,
            };
        case NotificationType.APPOINTMENT_DECLINE_SUCCESS:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ERROR,
                text: `Du hast den Termin mit ${therapistInAppointment(n)} am ${getAppointmentScheduledStart(n)} erfolgreich abgelehnt.`,
            };
        case NotificationType.CALL_CANCELED:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ERROR,
                text: `Dein Termin mit ${therapistInCall(n)} am ${getScheduledStart(n)} wurde abgesagt.`,
            };
        case NotificationType.CALL_BEGINNING_REMINDER:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.WARNING,
                text: `Dein Termin mit ${therapistInCall(n)} am ${getScheduledStart(n)} beginnt in 24 Stunden.`,
            };
        case NotificationType.CALL_THERAPIST_HANDOVER_ACTIVE:
            return {
                icon: DEFAULT_ICON,
                variant: VARIANT.ACTIVE,
                text: `${getActorText(n)} einen Termin am ${getScheduledStart(n)} mit ${getPatientText(n)} zugewiesen.`,
            };
        case NotificationType.CALL_THERAPIST_HANDOVER_PASSIVE_PATIENT:
            return {
                icon: DEFAULT_ICON,
                variant: VARIANT.ACTIVE,
                text: `Dein Termin am ${getScheduledStart(n)} wurde von ${newTherapistName(n)} übernommen.`,
            };
        case NotificationType.CALL_THERAPIST_HANDOVER_PASSIVE_THERAPIST:
            return {
                icon: DEFAULT_ICON,
                variant: VARIANT.ACTIVE,
                text: `Dein Termin mit ${getPatientText(n)} am ${getScheduledStart(n)} wurde von ${newTherapistName(n)} übernommen.`,
            };
        case NotificationType.COMPLETED_EXERCISE_PROGRAM:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ACTIVE,
                text: `Gratulation! Du hast das Programm #${programIndex(n)} erfolgreich abgeschlossen.`,
            };
        case NotificationType.CRITICAL_PROGRAM_VALUES:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ERROR,
                text: `ACHTUNG: Kritischer Wert bei ${actorName(n)} in Programm #${programIndex(n)}.`,
                buttons: [
                    {
                        text: 'Zur Statistik',
                        colorVariant: VARIANT.ACTIVE,
                        actionKey: 'GO_TO_PATIENT_STATISTICS',
                    },
                ],
            };
        case NotificationType.OFFICE_USER_REGISTER:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ACTIVE,
                text: `${actorName(n)} hat sich als Mitarbeiter:in registriert.`,
            };
        case NotificationType.EXERCISE_PROGRAM_COMPLETION_REMINDER:
            return {
                icon: DEFAULT_ICON,
                variant: VARIANT.WARNING,
                text: 'Erinnerung! Du hast das heutige Programm noch nicht vollständig absolviert.',
            };
        case NotificationType.EXERCISE_PROGRAM_UPDATED:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ACTIVE,
                ...withPatientMessage(`Update! ${actorName(n)} hat das Programm #${programIndex(n)} aktualisiert.`, n),
                buttons: [exerciseProgramButton(n, 'Zum Programm')],
            };
        case NotificationType.NEW_EXERCISE_PROGRAM:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ACTIVE,
                ...withPatientMessage('Neues Übungsprogramm verfügbar.', n),
                buttons: [exerciseProgramButton(n, 'Jetzt starten')],
            };
        case NotificationType.NEW_OFFICE_EXERCISE:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ACTIVE,
                text: `${actorName(n)} hat eine neue Übung in der Bibliothek veröffentlicht.`,
                buttons: [
                    { text: 'Zur Bibliothek', colorVariant: VARIANT.ACTIVE, href: '/exercises/library' },
                ],
            };
        case NotificationType.NEW_PROGRAM_STATS:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ACTIVE,
                text: `Neue Statistik zu Programm #${programIndex(n)} von ${actorName(n)} im Patientenprofil vorhanden.`,
            };
        case NotificationType.PATIENT_HAND_OVER_AS_PATIENT:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ACTIVE,
                text: `Du wurdest an ${getActorSalutationDativ(n)} ${n.actor?.lastName ?? ''} überwiesen.`,
            };
        case NotificationType.PATIENT_HAND_OVER:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ACTIVE,
                text: `Patient ${actorName(n)} wurde erfolgreich zugewiesen.`,
            };
        case NotificationType.PATIENT_REGISTER:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ACTIVE,
                text: `${actorName(n)} hat sich registriert.`,
            };
        case NotificationType.PATIENT_REMOVED:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ERROR,
                text: `${actorName(n)} wurde aus deiner Kartei entfernt.`,
            };
        case NotificationType.PROGRAM_EXPIRED:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ERROR,
                text: `Die Laufzeit des Programms #${programIndex(n)} von ${actorName(n)} ist abgelaufen.`,
                buttons: [
                    {
                        text: 'Zum Programm',
                        colorVariant: VARIANT.ACTIVE,
                        actionKey: 'GO_TO_PATIENT_PROGRAM',
                    },
                ],
            };
        case NotificationType.TODAYS_EXERCISE_SCHEDULE: {
            const count = n.exercisePrograms?.length ?? 0;
            const stillOpen = count === 1
                ? `Deine heutige Übungseinheit ${todaysScheduleLabel(n)} wartet noch`
                : `Deine ${count} heutigen Übungseinheiten ${todaysScheduleLabel(n)} warten noch`;
            return {
                icon: DEFAULT_ICON,
                variant: VARIANT.WARNING,
                // Evening nudge: name what is still open and that there is time left,
                // so it reads as an invitation rather than as a scolding.
                text: `${stillOpen}. Du hast Zeit, deine Genesung zu fördern.`,
                // One unit due → start it; several → the overview, matching the text's
                // own singular/plural split above.
                buttons: [exerciseProgramButton(n, 'Zu den Programmen')],
            };
        }
        case NotificationType.NEW_INVOICE_AVAILABLE:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ACTIVE,
                text: 'Es steht eine neue Rechnung zur Verfügung.',
                buttons: n.invoiceUrl
                    ? [{ text: 'Rechnung öffnen', colorVariant: VARIANT.ACTIVE, href: n.invoiceUrl }]
                    : undefined,
            };
        case NotificationType.NEW_PRODUCT_ASSIGNMENT:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ACTIVE,
                text: `${actorName(n)} hat eine Leistung im Praxis-Shop gekauft.`,
            };
        case NotificationType.PRODUCT_ASSIGNMENT_7_DAY_WARNING:
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ERROR,
                text: `Die erworbene Leistung von ${actorName(n)} läuft in 7 Tagen aus und sie wurde noch nicht vollständig erbracht.`,
            };
        case NotificationType.APPOINTMENT_RESCHEDULED: {
            let text = n.previousScheduledStart && n.newScheduledStart
                ? `Dein Termin wurde verschoben. Von ${getRescheduleFrom(n)} auf ${getRescheduleTo(n)}.`
                : 'Dein Termin wurde verschoben.';
            if (n.newTherapist) {
                const name = `${n.newTherapist.firstName ?? ''} ${n.newTherapist.lastName ?? ''}`.trim();
                text += ` Neue/r Therapeut/in: ${name}`;
            }
            return { icon: ICONS[n.type], variant: VARIANT.ACTIVE, text };
        }
        case NotificationType.APPOINTMENT_INVITE: {
            const showButtons = !n.appointment?.isCanceled && n.ownParticipantStatus === 'ausstehend';
            return {
                icon: ICONS[n.type],
                variant: VARIANT.ACTIVE,
                text: `${therapistInAppointmentNominativ(n)} hat dich zu einem Termin am ${getAppointmentScheduledStart(n)} eingeladen.`,
                buttons: showButtons
                    ? [
                        {
                            text: 'Ablehnen',
                            colorVariant: VARIANT.DESTRUCTIVE,
                            icon: 'icon-[mingcute--calendar-x-line]',
                            actionKey: 'APPOINTMENT_DECLINE',
                        },
                        {
                            text: 'Bestätigen',
                            colorVariant: VARIANT.MOST_IMPORTANT,
                            icon: 'icon-[mingcute--calendar-add-line]',
                            actionKey: 'APPOINTMENT_ACCEPT',
                        },
                    ]
                    : undefined,
            };
        }
        // Surfaced via push/email only in the legacy code; no in-app segment config
        // existed, so wording is ported from the backend switch and the icon falls
        // through to the default.
        case NotificationType.PATIENT_INVOICE_ISSUED:
            return {
                icon: DEFAULT_ICON,
                variant: VARIANT.ACTIVE,
                text: 'Deine Praxis hat eine neue Rechnung für dich ausgestellt.',
            };
        case NotificationType.PATIENT_INVOICE_CANCELLED:
            return {
                icon: DEFAULT_ICON,
                variant: VARIANT.ACTIVE,
                text: 'Deine Praxis hat eine Rechnung storniert und eine Stornorechnung ausgestellt.',
            };
        case NotificationType.HONORARVEREINBARUNG_SENT:
            return {
                icon: DEFAULT_ICON,
                variant: VARIANT.ACTIVE,
                text: 'Deine Praxis hat dir eine Honorarvereinbarung zugeschickt.',
            };
        // Patient self-booking confirm/decline (PRD #1343, RS-002).
        case NotificationType.BOOKING_CONFIRMED:
            return {
                icon: ICONS[NotificationType.BOOKING_CONFIRMED] ?? DEFAULT_ICON,
                variant: VARIANT.ACTIVE,
                text: 'Deine Praxis hat deinen angefragten Termin bestätigt.',
            };
        case NotificationType.BOOKING_DECLINED: {
            const reasonText = n.bookingDeclineReason
                ? BOOKING_DECLINE_REASON_TEXT[n.bookingDeclineReason]
                : null;
            return {
                icon: ICONS[NotificationType.BOOKING_DECLINED] ?? DEFAULT_ICON,
                variant: VARIANT.ERROR,
                text: reasonText
                    ? `Deine Praxis hat deinen angefragten Termin abgelehnt: ${reasonText}`
                    : 'Deine Praxis hat deinen angefragten Termin abgelehnt.',
            };
        }
        // Confirmation sweep auto-cancelled an unconfirmed hold (PRD #1343, RS-003).
        case NotificationType.BOOKING_AUTO_CANCELLED:
            return {
                icon: ICONS[NotificationType.BOOKING_AUTO_CANCELLED] ?? DEFAULT_ICON,
                variant: VARIANT.ERROR,
                text: 'Dein angefragter Termin wurde nicht rechtzeitig bestätigt und ist verfallen. Bitte kontaktiere deine Praxis, um einen neuen Termin zu vereinbaren.',
            };
        // SYSTEM_* and anything else: not surfaced as user content.
        default:
            return null;
    }
};
/**
 * The single content definition for a notification, or `null` for types that are
 * not surfaced to users (e.g. `SYSTEM_*`).
 */
export const getNotificationContent = (n) => {
    const body = getBody(n);
    if (!body)
        return null;
    return { title: TITLES[n.type] ?? '', ...body };
};
//# sourceMappingURL=notification-content.js.map